import React, { useCallback, useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions, GridReadyEvent, GridApi } from 'ag-grid-community';

import { AllCommunityModule, ModuleRegistry, themeMaterial } from 'ag-grid-community';

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

// Import AG Grid styles
// import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

export interface DataTableProps<TData = object> {
  /**
   * Data to be displayed in the table
   */
  rowData: TData[] | null;

  /**
   * Column definitions for the table
   */
  columnDefs: ColDef<TData>[];

  /**
   * Additional configuration options for the grid
   */
  gridConfig?: Partial<GridOptions<TData>>;

  /**
   * CSS class name for the grid container
   */
  className?: string;

  /**
   * Height of the grid in pixels or CSS value
   * If autoHeight is true, this will be ignored
   */
  height?: string | number;

  /**
   * Width of the grid in pixels or CSS value
   */
  width?: string | number;

  /**
   * Callback when grid is ready
   */
  onGridReady?: (params: GridReadyEvent<TData>) => void;

  /**
   * Theme to use for the grid ('ag-theme-alpine', 'ag-theme-balham', etc.)
   * Default: 'ag-theme-alpine'
   */
  theme?: string;

  /**
   * Whether to automatically adjust the height based on the number of rows
   * Default: false
   */
  autoHeight?: boolean;

  /**
   * Maximum height for the grid when autoHeight is true (in pixels or CSS value)
   * If not provided, the grid will expand to fit all rows
   */
  maxHeight?: string | number;

  /**
   * Whether data is currently loading
   * Default: false
   */
  isLoading?: boolean;

  /**
   * Custom loading message to display when data is loading
   * If not provided, AG Grid's default message will be used
   */
  loadingMessage?: string;

  /**
   * Custom no rows message to display when there's no data
   * If not provided, AG Grid's default message will be used
   */
  noRowsMessage?: string;
}

/**
 * DataTable component that wraps AG Grid functionality
 */
const DataTable = <TData extends object>({
  rowData,
  columnDefs,
  gridConfig = {},
  className = '',
  height = '600px',
  width = '100%',
  onGridReady,
  theme = 'ag-theme-alpine',
  autoHeight = false,
  maxHeight,
  isLoading = false,
  loadingMessage,
  noRowsMessage,
}: DataTableProps<TData>): React.ReactElement => {
  const [gridApi, setGridApi] = useState<GridApi<TData> | null>(null);

  // Handle grid ready event
  const handleGridReady = useCallback(
    (params: GridReadyEvent<TData>) => {
      setGridApi(params.api);

      // Call the onGridReady callback if provided
      if (onGridReady) {
        onGridReady(params);
      }

      // Auto-size columns after data is loaded
      params.api.sizeColumnsToFit();
    },
    [onGridReady]
  );

  // Update loading state when isLoading or rowData changes
  useEffect(() => {
    if (!gridApi) return;

    // Use setGridOption to update the loading state
    gridApi.setGridOption('loading', isLoading);
  }, [gridApi, isLoading, rowData]);

  // Resize columns when window size changes
  useEffect(() => {
    const handleResize = () => {
      if (gridApi) {
        gridApi.sizeColumnsToFit();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [gridApi]);

  // Default grid options
  const defaultGridOptions: Partial<GridOptions<TData>> = {
    theme: themeMaterial,
    pagination: true,
    paginationPageSize: 50,
    paginationAutoPageSize: false,
    suppressCellFocus: false,
    rowSelection: undefined, // Disable row selection by default
    enableCellTextSelection: true,
    animateRows: true,
    paginationPageSizeSelector: [50, 100, 200, 500],
    // Set initial loading state
    loading: isLoading,
  };

  // Set custom overlay messages if provided
  if (loadingMessage) {
    defaultGridOptions.loadingCellRendererParams = {
      loadingMessage,
    };
  }

  if (noRowsMessage) {
    defaultGridOptions.noRowsOverlayComponentParams = {
      noRowsMessage,
    };
  }

  // Add domLayout for autoHeight if enabled
  if (autoHeight) {
    defaultGridOptions.domLayout = 'autoHeight';
  }

  // Combine default options with provided config
  const gridOptions: Partial<GridOptions<TData>> = {
    ...defaultGridOptions,
    ...gridConfig,
  };

  // Determine the style for the container
  const containerStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
  };

  // Only set height if autoHeight is false or maxHeight is provided
  if (!autoHeight) {
    containerStyle.height = typeof height === 'number' ? `${height}px` : height;
  } else if (maxHeight) {
    containerStyle.maxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
    containerStyle.overflow = 'auto';
  }

  return (
    <div className={`${theme} ${className}`} style={containerStyle}>
      <AgGridReact
        rowData={rowData || []}
        columnDefs={columnDefs}
        onGridReady={handleGridReady}
        {...gridOptions}
      />
    </div>
  );
};

export default DataTable;
